import React from 'react';
import { Row, Col } from 'reactstrap';
import { toast } from 'react-toastify';
import { isEqual } from 'lodash-es';
import { v7 as uuid } from 'uuid';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faCheckCircle, faWarehouse, faTimes,
} from '@fortawesome/free-solid-svg-icons';
import { FormPrompt } from '@s20/components/common/FormPrompt';
import { clone } from '@s20/common/util/object';
import { httpClient } from '@s20/services/httpClient';
import {
    AppPageForm,
    FlexCenterRow,
    FormDivider,
    FormGroupColumn,
    GroupedRow,
    onFieldChange,
    onReactSelectChanged,
    SubHeading,
    ToastMessage,
    SimpleTable,
    Input,
    NumericInput,
    FormCheckbox,
} from '../common/forms/FormElements';
import { BaseFormViewModel } from '../common/ViewModel';
import CommonContext,
{
    ApiRoutes,
    ApplicationPermissions,
    AppNavPaths,
    ChargeTypeResourceTypes,
} from '../Common';
import { handleFormSaveError } from '../common/forms/ValidationError';
import ChargeTypeSelect from '../chargeType/ChargeTypeSelect';
import { NotAuthorizedPage } from '../status/StatusCodes';
import { SaveButton } from '../common/forms/SaveButton';
import { ResourceKit } from './ResourceKit';

class ResourceKitForm extends React.Component {
    static contextType = CommonContext;

    constructor(props) {
        super(props);
        this.formRef = React.createRef();
        this.chargeTypeSelectorRef = React.createRef();

        const stateBase = {
            kit: new ResourceKit(),
            ...new BaseFormViewModel(),
        };

        this.state = stateBase;
        this.onSelectChanged = this.onSelectChanged.bind(this);
        this.onSubmit = this.onSubmit.bind(this);
        this.onChange = this.onChange.bind(this);
    }

    componentDidMount() {
        return this.populateState();
    }

    componentDidUpdate(prevProps) {
        if (prevProps && (this.props.match.params.id !== (prevProps.match.params ?? {}).id)) {
            return this.populateState();
        }
    }

    // #region CHARGE TYPES
    onAddChargeTypes = () => {
        const { kit } = { ...this.state };
        const { chargeTypes } = { ...kit };
        const typesToOmit = (chargeTypes ?? []).map((c) => c.chargeTypeId) ?? [];
        // pass in types already selected
        this.chargeTypeSelectorRef.current.open(null, [...typesToOmit]);
    };

    onAddChargeTypesCallback = async (selections) => {
        const { kit } = { ...this.state };
        const resources = [...kit.resources] ?? [];

        const newChargeTypesDetails = await httpClient.post(ApiRoutes.chargeType.byIds(), [...selections].map((x) => x.id));

        // add charge type resources
        newChargeTypesDetails.forEach((ct) => {
            if (ct.resourceTypeId == ChargeTypeResourceTypes.Setup) {
                const existingResource = resources.find((rs) => rs.resourceId == ct.id && rs.resourceTypeId == ChargeTypeResourceTypes.Setup);

                if (!existingResource) {
                    const chargeType = {
                        id: uuid(),
                        chargeTypeId: ct.id,
                        description: ct.description,
                        resourceTypeId: ct.resourceTypeId,
                    };

                    resources.push(
                        {
                            id: uuid(),
                            quantity: 1,
                            resourceId: ct.id,
                            resourceName: ct.description,
                            resourceTypeId: ct.resourceTypeId,
                            resourceTypeName: 'Setup',
                            chargeTypes: [chargeType],
                        },
                    );
                }
            } else if (ct.resourceTypeId == ChargeTypeResourceTypes.Employee && ct.applicationGroup != null) {
                const existingResource = resources.find((rs) => rs.resourceId == ct.applicationGroup.id && rs.resourceTypeId == ChargeTypeResourceTypes.Employee);

                const chargeType = {
                    id: uuid(),
                    chargeTypeId: ct.id,
                    description: ct.description,
                    resourceTypeId: ct.resourceTypeId,
                };

                if (existingResource) {
                    existingResource.chargeTypes.push(chargeType);
                } else {
                    resources.push(
                        {
                            id: uuid(),
                            quantity: 1,
                            resourceId: ct.applicationGroup.id,
                            resourceName: ct.applicationGroup.groupName,
                            resourceTypeId: ct.resourceTypeId,
                            resourceTypeName: 'Group',
                            chargeTypes: [chargeType],
                        },
                    );
                }
            } else if (ct.equipmentTypes?.length > 0) {
                ct.equipmentTypes.forEach((et) => {
                    const resourceTypeName = ct.resourceTypeId == ChargeTypeResourceTypes.Equipment ? 'Equipment' : 'Rental';
                    const existingResource = resources.find((rs) => rs.resourceId == et.id && rs.resourceTypeName == resourceTypeName);

                    const chargeType = {
                        id: uuid(),
                        chargeTypeId: ct.id,
                        description: ct.description,
                        resourceTypeId: ct.resourceTypeId,
                    };

                    if (existingResource) {
                        existingResource.chargeTypes.push(chargeType);
                    } else {
                        const nextId = uuid();

                        resources.push(
                            {
                                id: nextId,
                                resourceId: et.id,
                                resourceName: et.description,
                                resourceTypeId: ct.resourceTypeId,
                                resourceTypeName,
                                chargeTypes: [chargeType],
                                quantity: 1,
                            },
                        );
                    }
                });
            }
        });

        this.setState({
            kit: {
                ...kit,
                resources,
            },
        });
    };

    onChange = onFieldChange;

    onClearErrors = () => this.setState({ errors: {} });

    onRemoveChargeType = (chargeTypeId) => {
        const { kit } = { ...this.state };
        const resources = [...kit.resources];

        // Find the resource linked to this charge type
        const chargeResources = resources.filter((rs) =>
            rs.chargeTypes.some((ct) => ct.id == chargeTypeId),
        );

        chargeResources.forEach((cr) => {
            if (cr) {
                if (cr.chargeTypes.length === 1) {
                    // Remove the whole resource if it only has this charge type
                    resources.splice(resources.indexOf(cr), 1);
                } else {
                    // Remove only the charge type from the resource
                    cr.chargeTypes = cr.chargeTypes.filter(
                        (cr) => cr.id !== chargeTypeId,
                    );
                }
            }
        });

        this.setState({
            kit: {
                ...kit,
                resources,
            },
        });
    };

    onSelectChanged = onReactSelectChanged;

    onSubmit = async () => {
        // Clear any fluent api errors
        this.onClearErrors();
        this.setSaving(true);

        const { kit } = this.state;

        const cloned = clone(kit);

        this.setState({ originalData: cloned });

        await this.updateResourceKit(cloned);
    };

    setSaving = (b) => this.setState({ saving: b });

    handleSaveError = (err) => handleFormSaveError(this, err);

    async populateState() {
        const kitId = this.props.match.params.id;

        const [kit] = await Promise.all([
            kitId ? httpClient.js(ApiRoutes.resourceKit.byId(kitId)) : new ResourceKit(),
        ]);

        const originalData = clone(kit);

        this.setState({
            originalData,
            kit,
            existing: !!kitId,
            loading: false,
            saving: false,
        });
    }

    resetForm = () => this.setState((prevState) => ({ formValidated: false, originalData: clone(prevState.kit) }));

    updateResourceKit = async (kit) => {
        const response = await httpClient.put(ApiRoutes.resourceKit.byId(kit.id), kit)
            .catch(this.handleSaveError);
        if (response && parseInt(response.data ?? 0) >= 0) {
            toast.success(
                <ToastMessage
                    icon={faCheckCircle}
                    header="Save Successful"
                    message={`Resource Kit ${kit.name} saved.`}
                />,
            );

            this.context.history.push(`${AppNavPaths.ResourceKit}/${response.data}`);
        }

        this.setSaving(false);
    };

    renderChargeTypesRows = () => {
        const { kit } = { ...this.state };
        const { resources } = kit;

        if (!resources) return '';

        const resourceTypes = ['', 'Employee', 'Equipment', 'None', 'Planning', 'Setup', 'Rental'];

        const uniqueChargeTypes = [
            ...new Map(
                resources.flatMap((rs) => rs.chargeTypes).map((ct) => [ct.chargeTypeId, ct]),
            ).values(),
        ];

        return uniqueChargeTypes.map((chargeType, chargeTypeIndex) => (
            <tr
                key={chargeTypeIndex}
                style={{ maxHeight: '10px' }}
            >
                <td style={{ verticalAlign: 'middle' }}>
                    {chargeType.description}
                </td>
                <td style={{ verticalAlign: 'middle' }}>
                    {resourceTypes[chargeType.resourceTypeId ?? 0]}
                </td>
                <td
                    style={{ verticalAlign: 'middle' }}
                    className="text-center"
                >
                    <FontAwesomeIcon
                        icon={faTimes}
                        title={`Remove ${chargeType.description}`}
                        className="text-danger cursor-pointer"
                        onClick={() => this.onRemoveChargeType(chargeType.id)}
                    />
                </td>
            </tr>
            ));
    };

    renderResourcesRows = () => {
        const { kit } = { ...this.state };
        const { resources } = kit;
        if (!resources) return '';
        return resources.map((resource, resourceIndex) => (
            <tr
                key={resourceIndex}
                style={{ maxHeight: '10px' }}
            >
                <td style={{ verticalAlign: 'middle' }}>
                    {resource.resourceName}
                </td>
                <td style={{ verticalAlign: 'middle' }}>
                    {resource.resourceTypeName}
                </td>
                <td
                    className="text-center"
                    width="100"
                >
                    <div style={{ marginTop: '12px' }}>
                        <NumericInput
                            className="text-right"
                            name={`quantity${resourceIndex}`}
                            required
                            value={resource.quantity ?? ''}
                            onChange={(event) => {
                                    const val = event.target.value; const
                                        { kit } = { ...this.state };
                                    kit.resources[resourceIndex].quantity = val;
                                    this.setState({ kit });
                                }}
                            min={0}
                            step={1}
                            wholeNumber
                            preventNegative
                            error="Resource Quantity is required"
                        />
                    </div>
                </td>
            </tr>
            ));
    };

    render() {
        const {
            kit,
            existing,
            originalData,
            errors,
            formValidated,
            saving,
        } = this.state;

        const showConfirmationPrompt = (!isEqual(originalData, kit));
        const resourceKitsEnabled = this.context.checkUserHasPermission(ApplicationPermissions.resourceKit_view);

        const chargeTypeTableHeader = [
            { name: 'Charge Type' },
            { name: 'Resource Type' },
            { name: '', class: 'text-center' },
        ];

        const resourceTableHeader = [
            { name: 'Resource' },
            { name: 'Type' },
            { name: 'Quantity' },
        ];

        return (
            <>
                <FormPrompt
                    when={!!showConfirmationPrompt}
                    message="You have unsaved changes, are you sure you want to leave?"
                />

                {
                    !resourceKitsEnabled ? <NotAuthorizedPage />
                        : (
                            <>
                                <AppPageForm
                                    formShown={this.context.formIsOpen}
                                    formId="resourceKitForm"
                                    formHeadingIcon={faWarehouse}
                                    formHeading={!kit.id ? 'New Resource Kit' : 'Edit Resource Kit'}
                                    formName="resourceKitForm"
                                    formRef={this.formRef}
                                    onSubmit={this.onSubmit}
                                    setIsValidated={(value) => {
                                        this.setState({ formValidated: value });
                                    }}
                                    isValidated={formValidated}
                                    saving={saving}
                                    errors={errors}
                                    onClearErrors={this.onClearErrors}
                                    loading={this.state.loading}
                                    backUrl={AppNavPaths.ResourceKits}
                                    backLabel="Back to Resource Kits"
                                >
                                    <GroupedRow>
                                        <FormGroupColumn>
                                            <Input
                                                label="Name"
                                                id="name"
                                                name="kit.name"
                                                value={kit.name ?? ''}
                                                onChange={this.onChange}
                                                placeholder="Enter Name"
                                                required
                                                error="Kit name is required."
                                            />
                                            <Input
                                                label="Description"
                                                id="description"
                                                name="kit.description"
                                                value={kit.description ?? ''}
                                                onChange={this.onChange}
                                                placeholder="Enter Description"
                                            />
                                            <FormCheckbox
                                                className="w-auto"
                                                id="isActive"
                                                name="kit.isActive"
                                                checked={kit.isActive ?? ''}
                                                onChange={this.onChange}
                                                labelText="Is Active"
                                            />
                                        </FormGroupColumn>
                                    </GroupedRow>
                                    <Row>
                                        <Col>
                                            <SubHeading>Charge Types</SubHeading>
                                            <SimpleTable
                                                className="mb-0"
                                                id="chargeTypesTable"
                                                onAddFunction={this.onAddChargeTypes}
                                                addButtonEnabled
                                                addLabelText={this.context.formIsOpen ? (
                                                    <span>
                                                        <i className="fa fa-spin fa-circle-notch mr-1" />
                                                        Opening charge types form...
                                                    </span>
                                                ) : 'Add Charge Types'}
                                                noTopBorder
                                                permission="chargetype"
                                                tableHeaderLabels={chargeTypeTableHeader}
                                                editable={false}
                                                entities={(this.state.kit ?? {}).resources?.flatMap((rs) => rs.chargeTypes) ?? []}
                                                rowRenderer={this.renderChargeTypesRows}
                                                noDataText="No charge types found."
                                            />
                                        </Col>
                                    </Row>

                                    <Row className="mt-4">
                                        <Col>
                                            <SubHeading>Resources</SubHeading>
                                            <SimpleTable
                                                className="mb-0"
                                                id="resourcesTable"
                                                permission="chargetype"
                                                noTopBorder
                                                tableHeaderLabels={resourceTableHeader}
                                                editable={false}
                                                entities={(this.state.kit ?? {}).resources ?? []}
                                                rowRenderer={this.renderResourcesRows}
                                                noDataText="No resources found."
                                            />
                                        </Col>
                                    </Row>
                                    <FormDivider />

                                    <FlexCenterRow className="mb-3">
                                        <SaveButton
                                            saving={!!this.state.saving}
                                            label={!existing ? 'Save New Resource Kit' : 'Save'}
                                        />
                                    </FlexCenterRow>
                                </AppPageForm>

                                <ChargeTypeSelect
                                    ref={this.chargeTypeSelectorRef}
                                    id="chargeTypeSelector"
                                    title="Add Charge Type to Resource Kit"
                                    onAddCallback={this.onAddChargeTypesCallback}
                                    billableOnly
                                />
                            </>
                        )

                }
            </>
        );
    }
}
export default ResourceKitForm;
